import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import type { ReleaseListRow as ReleaseRowData } from 'fym-shared/src/music24/releases';
import { TypedMemo } from '../../../../common/types';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { SMLLink } from '../../../components/SMLLink/SMLLink';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { daysUntilRelease } from '../releaseOrder';
import { formatReleaseWhen } from '../releaseWhen';
import { releaseMessages } from '../releaseMessages';
import { ReleaseArtwork } from '../ReleaseArtwork';
import { ReleaseNextStepLine } from './ReleaseNextStepLine';
import { ReleaseRowCounts } from './ReleaseRowCounts';
import { releaseListMessages } from './releaseListMessages';
import { isDraftItem } from './releaseDraft';
import { useDraftFinishSetupPath } from './useDraftFinishSetupPath';
import { FinishSetupRowLink } from './FinishSetupRowLink';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './ReleasesList.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReleaseListRowProps {
  item: CatalogItem;
  row: ReleaseRowData;
  now: Date;
  /** The soonest upcoming release gets the primary "Open" (design a1). */
  isPrimary?: boolean;
}

/**
 * One release of the list (design a1): the cover, the title and the release day, the next step,
 * the counts and "Open". The whole row is one link to the release Overview, so a click, Enter and a
 * middle click (a new tab) work anywhere on it. The row holds no other control.
 *
 * A draft (a typed song with only a title, owner answer 1 of 2026-10-09) has a "Draft" pill, and its
 * row is the "Finish setup" link to the next step of the "Add a song" page. In the native web view
 * the draft row opens the release like every row (the apps keep their own add-song screens).
 */
const ReleaseListRowComponent: React.FC<ReleaseListRowProps> = ({ item, row, now, isPrimary = false }) => {
  const intl = useIntl();
  const days = daysUntilRelease(item.releaseDate, now);
  const isUpcoming = days !== null && days >= 0;
  const byLine = useMemo(
    () =>
      [
        item.artistName,
        item.releaseType,
        item.kind === CatalogItemKind.unreleased ? intl.formatMessage(releaseMessages.unreleased) : null,
      ]
        .filter((part): part is string => part !== null && part !== '')
        .join(' · '),
    [intl, item.artistName, item.kind, item.releaseType]
  );
  const params = useMemo(() => ({ catalogItemUuid: item.uuid }), [item.uuid]);
  const isDraft = isDraftItem(item);
  const finishSetupPath = useDraftFinishSetupPath(item);

  const content = (
    <>
      <ReleaseArtwork item={item} className={styles.cover} />
      <div className={styles.grow}>
        <span className={styles.titleLine}>
          <span className={styles.songTitle}>{item.title}</span>
          {isDraft && <span className={styles.draftPill}>{intl.formatMessage(releaseListMessages.draft)}</span>}
        </span>
        <span className={styles.by}>{byLine}</span>
        <div className={clsx(styles.when, isUpcoming && styles.whenUp, days === null && styles.whenNone)}>
          {formatReleaseWhen(intl, item.releaseDate, days)}
        </div>
      </div>
      <ReleaseNextStepLine step={row.nextStep} />
      <ReleaseRowCounts row={row} isUpcoming={isUpcoming} />
      <span
        className={clsx(styles.openLook, (isPrimary || finishSetupPath !== null) && styles.openLookPrimary)}
        aria-hidden="true"
      >
        {intl.formatMessage(finishSetupPath !== null ? releaseListMessages.finishSetup : releaseListMessages.open)}
      </span>
    </>
  );

  if (finishSetupPath !== null) {
    return (
      <FinishSetupRowLink
        path={finishSetupPath}
        className={styles.listRow}
        ariaLabel={intl.formatMessage(releaseListMessages.finishSetupLabel, { title: item.title })}
      >
        {content}
      </FinishSetupRowLink>
    );
  }

  return (
    <SMLLink
      to={SMLRoutes.promoteRelease}
      params={params}
      className={styles.listRow}
      ariaLabel={intl.formatMessage(releaseListMessages.openLabel, { title: item.title })}
    >
      {content}
    </SMLLink>
  );
};

export const ReleaseListRow = TypedMemo(ReleaseListRowComponent);
